import React, { ComponentProps } from "react";
import Styles from "./AssuredLoadStatus.module.scss";
// Components
import Typography from "@vahak/core-ui/dist/components/Typography";
import TapNgoTimer from "./tap-n-go-timer/TapNgoTimer";
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import HowItWorksBtn from "./how-it-works/HowItWorksBtn";
import PaymentBtn from "../marketplace/load-bid-form-new/bid-form/assured-load/PaymentBtn";
import AssuredHelpDesk from "./helpdesk-wrapper/AssuredHelpDesk";

// Constants
import { AssuredLoadBidMeta } from "./helper";
import { LoadBookingInfo } from "@vahak/core/dist/custom-types/common-preloads";
import { BID_STATUS } from "@vahak/core/dist/constants/bidStatus";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { BookingAmountOrderRequest, getRequiredBookingForType } from "../../services/vas/useGeneratePayment";
import { AssuredBidStatusList } from "../admin/ticket-system-helper/assuredLoad";
// Methods
import { getSecondsLeftFromNow } from "../../methods/time";
// Hooks
// Assets
import BellIcon from "@vahak/core/dist/icons/bell.svg";
import GreenTick from "@vahak/core/dist/icons/GreenBackgroundSolidWhiteTick.svg";
import classnames from "classnames";
import { IEventTrackerProp } from "../../types/events";

interface AssuredLoadStatusProps {
    meta: AssuredLoadBidMeta;
    cId?: number;
    source: "bid" | "load" | "lorry";
    workingListType?: ComponentProps<typeof HowItWorksBtn>["workingListType"];
    wrapperClassName?: string;
    trackEvent: IEventTrackerProp["trackEvent"];
}

const AssuredLoadStatus = ({
    meta,
    cId,
    source,
    workingListType,
    wrapperClassName,
    trackEvent
}: AssuredLoadStatusProps) => {
    let bookingInfo: LoadBookingInfo = {};
    const isTapNgo = meta?.is_tap_n_go;
    const isSpot = meta?.is_spot_load;
    if (isTapNgo) {
        bookingInfo = meta?.tap_n_go_info ?? {};
    } else if (isSpot) {
        bookingInfo = meta?.spot_load_info ?? {};
    }

    const {
        load_owner_timer_duration = 0,
        lorry_owner_timer_duration = 0,
        is_load_owner_paid,
        timer_duration = 0
    } = bookingInfo;

    const need2PayBalance =
        bookingInfo?.is_lorry_owner_token_paid && is_load_owner_paid && !bookingInfo?.is_lorry_owner_balance_paid;
    const isLorryOwnerPaid = bookingInfo?.is_lorry_owner_balance_paid || bookingInfo?.is_lorry_owner_paid;
    const { updated_at_timestamp = 0, bid_status, loadCid, lorryCid } = meta;

    let timeLeft = 0;
    let showPaymentBtn = false;

    let descText: React.ReactNode = null;
    let leftIcon: React.ReactNode = null;
    let bottomTextHighlight: React.ReactNode = null;

    const isLoadView = cId === loadCid;
    const isLorryView = cId === lorryCid;

    const lorryAmount = need2PayBalance
        ? bookingInfo?.lorry_owner_remaining_amount
        : bookingInfo?.lorry_owner_booking_amount;

    const bookingData: BookingAmountOrderRequest = {
        ...meta?.bookingOrderInfo,
        bid_id: meta?.bidId,
        load_id: meta?.loadId,
        booking_for: getRequiredBookingForType({
            isTapNGo: meta?.is_tap_n_go,
            isSpot: meta?.is_spot_load,
            isTokenFlow: !!bookingInfo?.is_lorry_owner_token_paid,
            isTokenPaid: bookingInfo?.is_lorry_owner_token_paid,
            isBalancePaid: bookingInfo?.is_lorry_owner_balance_paid
        }),
        amount: isLoadView ? bookingInfo?.load_owner_booking_amount : lorryAmount
    };

    const getPaymentBtn = () => {
        return (
            <PaymentBtn
                trackEvent={trackEvent}
                bookingAmount={bookingData?.amount}
                bookingData={bookingData}
                paymentType={isLoadView ? "assured-security-deposit" : "assured-booking-amount"}
            />
        );
    };

    const HowItWorkSlot = !!workingListType && <HowItWorksBtn workingListType={workingListType} responsive />;
    const HelpSlot = <AssuredHelpDesk responsive callBackSrc="assured" />;

    if (isTapNgo && bid_status === BID_STATUS.WAITING && (isLoadView || !isLorryView)) {
        return (
            <div className={classnames(Styles.waitingAction, source && Styles[source])}>
                <div className={Styles.iconWrapper}>
                    <BellIcon />
                    <Typography size="s" mSize="xs" weight="semibold">
                        Waiting for acceptance from lorry owner
                    </Typography>
                </div>
                <span className={Styles.misc}>{HowItWorkSlot}</span>
            </div>
        );
    }

    const getTapNGoLoadStatus = () => {
        if (isTapNgo) {
            switch (bid_status) {
                case BID_STATUS.STAND_BY: {
                    timeLeft = getSecondsLeftFromNow(updated_at_timestamp + lorry_owner_timer_duration);
                    if (isLorryView) {
                        showPaymentBtn = isLorryView;
                        bottomTextHighlight = (
                            <Typography color={COLORS.ORANGE} size="xs" mSize="xs">
                                *Amount will be refunded if Shipper doesn’t confirm
                            </Typography>
                        );
                        descText = (
                            <TapNgoTimer
                                timerTemplate={"Pay your Booking amount in [timer] to confirm your lorry"}
                                timeLeft={timeLeft}
                                active
                            />
                        );
                    } else {
                        leftIcon = <GreenTick />;
                        descText = (
                            <TapNgoTimer
                                timerTemplate={
                                    "Lorry owner agreed the fixed price, please wait until lorry owner pays the booking amount in [timer]"
                                }
                                timeLeft={timeLeft}
                                active
                            />
                        );
                    }
                    break;
                }
                case BID_STATUS.PARTIALLY_ACCEPTED: {
                    timeLeft = getSecondsLeftFromNow(updated_at_timestamp + load_owner_timer_duration);
                    leftIcon = <GreenTick />;

                    if (isLoadView) {
                        showPaymentBtn = isLoadView;
                        descText = (
                            <>
                                <Typography color={COLORS.GREEN_700}>Lorry owner confirmed the booking, </Typography>
                                <TapNgoTimer
                                    timerTemplate={" pay your security deposit in [timer]"}
                                    timeLeft={timeLeft}
                                    active
                                />
                            </>
                        );
                    } else {
                        bottomTextHighlight = (
                            <Typography color={COLORS.ORANGE} size="xs" mSize="xs">
                                *Amount will be refunded if Shipper doesn’t confirm
                            </Typography>
                        );
                        descText = (
                            <TapNgoTimer
                                timerTemplate={
                                    "Waiting for the shipper to confirm booking. We will notify you once booking is confirmed in [timer]"
                                }
                                timeLeft={timeLeft}
                                active
                            />
                        );
                    }
                    break;
                }
                case BID_STATUS.IN_TRANSIT: {
                    if (need2PayBalance) {
                        timeLeft = getSecondsLeftFromNow(bookingInfo?.balance_payment_deadline || 0);
                        if (isLorryView) {
                            showPaymentBtn = isLorryView;
                            descText = (
                                <TapNgoTimer
                                    timerTemplate={"Pay remaining amount in [timer] to confirm this load"}
                                    timeLeft={timeLeft}
                                    active
                                />
                            );
                        } else {
                            // leftIcon = <GreenTick />;
                            // descText = (
                            //     <TapNgoTimer
                            //         timerTemplate={
                            //             "Lorry owner agreed the fixed price, please wait until lorry owner pays the booking amount in [timer]"
                            //         }
                            //         timeLeft={timeLeft}
                            //         active
                            //     />
                            // );
                        }
                    }

                    break;
                }
            }
        } else if (isSpot) {
            if (AssuredBidStatusList.includes(bid_status!)) {
                const is3rdPersonView = ![loadCid, lorryCid].includes(cId);
                if (!isLorryOwnerPaid && !is_load_owner_paid && is3rdPersonView) {
                    timeLeft = getSecondsLeftFromNow(updated_at_timestamp + timer_duration);
                    descText = (
                        <TapNgoTimer timerTemplate={"Booking amount not paid [timer]"} timeLeft={timeLeft} active />
                    );
                } else if (isLorryView || is3rdPersonView) {
                    showPaymentBtn = isLorryView && !isLorryOwnerPaid;
                    bottomTextHighlight = isLorryView && (
                        <Typography color={COLORS.ORANGE} size="xs" mSize="xs">
                            *Amount will be refunded if Shipper doesn’t confirm
                        </Typography>
                    );
                    leftIcon = isLorryOwnerPaid && <GreenTick />;
                    descText = isLorryOwnerPaid ? (
                        <TapNgoTimer
                            timerTemplate={
                                "Waiting for the shipper to confirm booking. We will notify you once booking is confirmed in [timer]"
                            }
                            timeLeft={getSecondsLeftFromNow(updated_at_timestamp + load_owner_timer_duration)}
                            active
                        />
                    ) : (
                        <TapNgoTimer
                            timerTemplate={"Pay your Booking amount in [timer] to confirm your lorry"}
                            timeLeft={getSecondsLeftFromNow(updated_at_timestamp + lorry_owner_timer_duration)}
                            active
                        />
                    );
                } else {
                    leftIcon = is_load_owner_paid && <GreenTick />;
                    showPaymentBtn = isLoadView && !is_load_owner_paid;
                    descText = is_load_owner_paid ? (
                        <TapNgoTimer
                            timerTemplate={
                                "Lorry owner agreed the fixed price, please wait until lorry owner pays the booking amount in [timer]"
                            }
                            timeLeft={getSecondsLeftFromNow(updated_at_timestamp + lorry_owner_timer_duration)}
                            active
                        />
                    ) : (
                        <TapNgoTimer
                            timerTemplate={"Pay your security deposit in [timer]"}
                            timeLeft={getSecondsLeftFromNow(updated_at_timestamp + load_owner_timer_duration)}
                            active
                        />
                    );
                }
            }
        }
        return (
            <div className={classnames(Styles.wrapper, wrapperClassName, source && Styles[source])}>
                <div className={Styles.desc}>
                    <Flex alignItems="flex-start" gap={4}>
                        <IconWrapper width={16} height={16} gap={"5px"}>
                            {leftIcon}
                        </IconWrapper>
                        <Typography size="s" mSize="xs" weight="semibold">
                            {descText}
                        </Typography>
                    </Flex>
                    {bottomTextHighlight}
                </div>
                <div className={classnames(Styles.action)}>
                    {HowItWorkSlot}
                    {HelpSlot}
                    {showPaymentBtn && <span className={Styles.payment}>{getPaymentBtn()}</span>}
                </div>
            </div>
        );
    };
    return <div className={Styles.main}>{getTapNGoLoadStatus()}</div>;
};

export default AssuredLoadStatus;
